import { DataResolutionEnum } from '@/src/enums/dataResolution.enum'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { useResizeObserver } from '@/src/hooks/useResizeObserver.hook'
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInWeeks,
  isValid,
  parseISO,
  setHours,
  setMinutes,
  startOfToday,
} from 'date-fns'
import type { RefCallback } from 'react'

enum GraphTimeFrameEnum {
  WITHIN_DAY = 'WITHIN_DAY',
  WITHIN_WEEK = 'WITHIN_WEEK',
  WITHIN_MONTH = 'WITHIN_MONTH',
  WITHIN_YEAR = 'WITHIN_YEAR',
  MULTIPLE_YEARS = 'MULTIPLE_YEARS',
}

export interface TickFormatting {
  dateFormat: string
  rotate: boolean
  showYear: boolean
  tickWidth: number
  fontSize?: string
  showTime?: boolean
}

const defaultTimeFrameMap = {
  [GranularityEnum.FIFTEEN_MINUTES]: GraphTimeFrameEnum.WITHIN_DAY,
  [GranularityEnum.ONE_HOUR]: GraphTimeFrameEnum.WITHIN_DAY,
  [GranularityEnum.ONE_DAY]: GraphTimeFrameEnum.WITHIN_WEEK,
  [GranularityEnum.ONE_WEEK]: GraphTimeFrameEnum.WITHIN_MONTH,
  [GranularityEnum.ONE_MONTH]: GraphTimeFrameEnum.WITHIN_YEAR,
  [GranularityEnum.ONE_YEAR]: GraphTimeFrameEnum.MULTIPLE_YEARS,
}

/**
 * Calculates the properties for X-axis ticks based on data points, resolution, granularity, and screen size.
 *
 * @param {Object} params - The parameters for the function.
 * @param {any[]} params.dataPoints - Array of data points to be displayed on the graph.
 * @param {string} params.datePropertyName - The name of the property in dataPoints that contains the date value.
 * @param {DataResolutionEnum} [params.dataResolution] - The time interval between data points in the dataset. If not provided, it will be inferred from the data points.
 * @param {GranularityEnum} [params.granularity] - The desired level of detail for display. If not provided, it will be determined automatically based on the date range of the data points.
 * @param {number} [params.tickWidth] - The width of each tick on the X-axis in pixels. If not provided, a default value will be used based on the determined granularity and time frame.
 *
 * @returns {Object} An object containing:
 *          - ref: A React ref for the graph container element.
 *          - xAxisInterval: The calculated interval between X-axis ticks.
 *          - granularity: The granularity used for calculation (either provided or automatically determined).
 *          - tickFormatProps: Formatting properties for the X-axis ticks.
 *
 * @note
 * Resolution vs. Granularity:
 * - Resolution refers to the frequency of data points in your dataset.
 * - Granularity refers to the desired frequency of ticks on the X-axis.
 *
 * Granularity Calculation:
 * If no granularity is provided, it will be automatically calculated based on the date range of the data points.
 * The calculation considers the number of days between the first and last data point, and selects an appropriate
 * granularity to provide a meaningful representation of the data.
 *
 * Data Resolution:
 * If not provided, the data resolution will be inferred from the data points. This should be correct in most use cases.
 * However, if you need to override this behavior, you can explicitly pass the dataResolution parameter.
 *
 * The function aims to respect the desired granularity while ensuring that the number of ticks
 * fits within the available screen space. It may increase the interval between ticks if necessary
 * to fit the data within the screen, but will always maintain multiples of the base granularity.
 */
export const useXAxisProps = ({
  dataPoints = [],
  datePropertyName,
  dataResolution,
  granularity,
  tickWidth,
}: {
  dataPoints: any[]
  datePropertyName: string
  dataResolution?: DataResolutionEnum
  granularity?: GranularityEnum
  tickWidth?: number
}) => {
  const thereIsDayOfTimeshift = dataPoints?.length % 96 !== 0
  const [graphContainerRef, dimensions] = useResizeObserver(1)
  const WIDTH_DIFFERENCE_BETWEEN_GRAPH_AND_TICK_CONTAINER = 100 // adjust if needed

  if (!dataPoints || dataPoints.length < 2 || dimensions.width === 0) {
    // Default values when no data points are provided
    const defaultGranularity = granularity ?? GranularityEnum.ONE_HOUR
    const defaultTimeFrame = defaultTimeFrameMap[defaultGranularity]
    const defaultTickProps = getTickFormatting(defaultGranularity, defaultTimeFrame)
    return {
      ref: graphContainerRef,
      xAxisInterval: 1,
      granularity: defaultGranularity,
      tickFormatProps: defaultTickProps,
      containerWidth: dimensions.width,
    }
  }

  // Extract dates from dataPoints
  const dates = dataPoints
    .filter(point => point[datePropertyName] !== null && point[datePropertyName] !== undefined)
    .map(point => {
      const dateValue = point[datePropertyName]
      return dateValue instanceof Date ? dateValue : parseDate(dateValue)
    })
    .sort((a, b) => a?.getTime() - b?.getTime())

  const startDate = dates[0]
  const endDate = dates[dates.length - 1]

  const numberOfDays = (endDate?.getTime() - startDate?.getTime()) / (1000 * 3600 * 24)
  const determinedDataPointCount = dataPoints.length
  const determinedGranularity = granularity || determineCustomGranularity(numberOfDays)

  const determinedDataResolution = dataResolution || calculateDataResolution(dates)
  if (isDataResolutionLowerThanRequestedGranularity(determinedDataResolution, determinedGranularity)) {
    console.warn(
      `Warning: Data resolution (${determinedDataResolution}) covers a longer period than requested granularity (${determinedGranularity}). The graph may not show the level of detail expected.`
    )
  }

  const timeFrame = determineTimeFrame(startDate, endDate)
  const tickFormatProps = getTickFormatting(determinedGranularity, timeFrame)
  const determinedTickWidth = tickWidth ?? tickFormatProps.tickWidth

  const maxTicksOnScreen = Math.floor((dimensions.width - WIDTH_DIFFERENCE_BETWEEN_GRAPH_AND_TICK_CONTAINER) / determinedTickWidth)

  // Calculate the base interval based on data resolution and granularity
  const baseInterval = calculateBaseInterval(determinedDataResolution, determinedGranularity)

  // Start with the base interval minus 1, but ensure it's at least 1
  let xAxisInterval = Math.max(baseInterval - 1, 0)

  // Increase the interval by adding baseInterval until it satisfies the screen size constraint
  const minInterval = Math.floor(determinedDataPointCount / maxTicksOnScreen)
  while (xAxisInterval < minInterval) {
    xAxisInterval += baseInterval
  }

  if (thereIsDayOfTimeshift) {
    xAxisInterval += 2
  }

  return {
    ref: graphContainerRef as RefCallback<HTMLDivElement>,
    xAxisInterval,
    granularity: determinedGranularity,
    tickFormatProps,
    containerWidth: dimensions.width,
  }
}

const determineTimeFrame = (startDate: Date, endDate: Date): GraphTimeFrameEnum => {
  const now = new Date()
  const diffInDays = (endDate?.getTime() - startDate?.getTime()) / (1000 * 3600 * 24)
  const isWithinCurrentYear = startDate?.getFullYear() === now?.getFullYear() && endDate?.getFullYear() === now?.getFullYear()
  const isWithinCurrentMonth = isWithinCurrentYear && startDate?.getMonth() === now?.getMonth() && endDate?.getMonth() === now?.getMonth()
  const isWithinCurrentWeek =
    isWithinCurrentMonth &&
    Math.floor((now?.getDate() - startDate?.getDate()) / 7) === Math.floor((now?.getDate() - endDate?.getDate()) / 7)

  if (diffInDays <= 1) return GraphTimeFrameEnum.WITHIN_DAY
  if (isWithinCurrentWeek) return GraphTimeFrameEnum.WITHIN_WEEK
  if (isWithinCurrentMonth) return GraphTimeFrameEnum.WITHIN_MONTH
  if (isWithinCurrentYear) return GraphTimeFrameEnum.WITHIN_YEAR
  return GraphTimeFrameEnum.MULTIPLE_YEARS
}

// Helper function to determine granularity based on # of days
const determineCustomGranularity = (numberOfDays: number): GranularityEnum => {
  if (numberOfDays > 730) return GranularityEnum.ONE_YEAR // More than 2 years
  if (numberOfDays > 60) return GranularityEnum.ONE_MONTH // More than 2 months
  if (numberOfDays > 14) return GranularityEnum.ONE_WEEK // More than 2 weeks
  if (numberOfDays > 2) return GranularityEnum.ONE_DAY // More than 2 days
  if (numberOfDays > 1 / 24) return GranularityEnum.ONE_HOUR // More than 1 hour
  return GranularityEnum.FIFTEEN_MINUTES
}

const calculateBaseInterval = (dataResolution: DataResolutionEnum, granularity: GranularityEnum) => {
  const resolutionToMinutes = {
    [DataResolutionEnum.FIFTEEN_MINUTES]: 15,
    [DataResolutionEnum.ONE_HOUR]: 60,
    [DataResolutionEnum.ONE_DAY]: 24 * 60,
    [DataResolutionEnum.ONE_WEEK]: 7 * 24 * 60,
    [DataResolutionEnum.ONE_MONTH]: 30 * 24 * 60,
    [DataResolutionEnum.ONE_YEAR]: 365 * 24 * 60,
  }

  const resolutionMinutes = resolutionToMinutes[dataResolution]

  switch (granularity) {
    case GranularityEnum.ONE_YEAR:
      return Math.max(1, Math.floor((365 * 24 * 60) / resolutionMinutes))
    case GranularityEnum.ONE_MONTH:
      return Math.max(1, Math.floor((30 * 24 * 60) / resolutionMinutes))
    case GranularityEnum.ONE_WEEK:
      return Math.max(1, Math.floor((7 * 24 * 60) / resolutionMinutes))
    case GranularityEnum.ONE_DAY:
      return Math.max(1, Math.floor((24 * 60) / resolutionMinutes))
    case GranularityEnum.ONE_HOUR:
      return Math.max(1, Math.floor(60 / resolutionMinutes))
    case GranularityEnum.FIFTEEN_MINUTES:
      return Math.max(1, Math.floor(15 / resolutionMinutes))
    default:
      return 1
  }
}

/**
 * Determines if the data resolution covers a longer time period than the requested granularity.
 *
 * @param dataResolution - The time interval between data points in the dataset.
 * @param granularity - The desired level of detail for display.
 * @returns true if dataResolution covers a longer period than requestedGranularity, false otherwise.
 *
 * @example
 * - If dataResolution is MONTHLY and requestedGranularity is DAILY, returns true.
 *   (Because monthly data can't provide daily detail)
 * - If dataResolution is HOURLY and requestedGranularity is DAILY, returns false.
 *   (Because hourly data can be aggregated to show daily detail)
 *
 * Note: In the context of time-based data:
 * - "Lower" resolution means longer time intervals (e.g., monthly, yearly).
 * - "Higher" resolution means shorter time intervals (e.g., hourly, daily).
 */
const isDataResolutionLowerThanRequestedGranularity = (resolution: DataResolutionEnum, granularity: GranularityEnum): boolean => {
  const resolutionOrder = [
    DataResolutionEnum.FIFTEEN_MINUTES,
    DataResolutionEnum.ONE_HOUR,
    DataResolutionEnum.ONE_DAY,
    DataResolutionEnum.ONE_WEEK,
    DataResolutionEnum.ONE_MONTH,
    DataResolutionEnum.ONE_YEAR,
  ]

  const granularityOrder = [
    GranularityEnum.FIFTEEN_MINUTES,
    GranularityEnum.ONE_HOUR,
    GranularityEnum.ONE_DAY,
    GranularityEnum.ONE_WEEK,
    GranularityEnum.ONE_MONTH,
    GranularityEnum.ONE_YEAR,
  ]

  return resolutionOrder.indexOf(resolution) > granularityOrder.indexOf(granularity)
}

// TODO: V Adjust tickWidths with usage and testing
const formatMapping: { [key in GraphTimeFrameEnum]: { [key in GranularityEnum]?: TickFormatting } } = {
  [GraphTimeFrameEnum.WITHIN_DAY]: {
    [GranularityEnum.FIFTEEN_MINUTES]: { dateFormat: 'HH:mm', rotate: true, showYear: false, tickWidth: 24 },
    [GranularityEnum.ONE_HOUR]: { dateFormat: 'HH:mm', rotate: true, showYear: false, tickWidth: 24 },
    [GranularityEnum.ONE_DAY]: { dateFormat: 'dd MMM', rotate: false, showYear: false, tickWidth: 60 },
  },
  [GraphTimeFrameEnum.WITHIN_WEEK]: {
    [GranularityEnum.FIFTEEN_MINUTES]: { dateFormat: 'EE HH:mm', rotate: true, showYear: false, tickWidth: 70 },
    [GranularityEnum.ONE_HOUR]: { dateFormat: 'EEE HH:mm', rotate: true, showYear: false, tickWidth: 70 },
    [GranularityEnum.ONE_DAY]: { dateFormat: 'dd MMM', rotate: false, showYear: false, tickWidth: 50 },
  },
  [GraphTimeFrameEnum.WITHIN_MONTH]: {
    [GranularityEnum.FIFTEEN_MINUTES]: { dateFormat: 'dd MMM HH:mm', rotate: false, showYear: false, tickWidth: 90 },
    [GranularityEnum.ONE_HOUR]: { dateFormat: 'dd MMM', rotate: false, showYear: false, showTime: true, tickWidth: 90 },
    [GranularityEnum.ONE_DAY]: { dateFormat: 'dd MMM', rotate: false, showYear: false, tickWidth: 60 },
    [GranularityEnum.ONE_WEEK]: { dateFormat: 'w', rotate: false, showYear: false, tickWidth: 50 },
  },
  [GraphTimeFrameEnum.WITHIN_YEAR]: {
    [GranularityEnum.FIFTEEN_MINUTES]: { dateFormat: 'dd MMM HH:mm', rotate: false, showYear: false, tickWidth: 90 },
    [GranularityEnum.ONE_HOUR]: { dateFormat: 'dd MMM', rotate: false, showYear: false, showTime: true, tickWidth: 90 },
    [GranularityEnum.ONE_DAY]: { dateFormat: 'dd MMM', rotate: false, showYear: false, tickWidth: 60 },
    [GranularityEnum.ONE_WEEK]: { dateFormat: 'w', rotate: false, showYear: false, tickWidth: 50 },
    [GranularityEnum.ONE_MONTH]: { dateFormat: 'MMM', rotate: false, showYear: false, tickWidth: 40 },
  },
  [GraphTimeFrameEnum.MULTIPLE_YEARS]: {
    [GranularityEnum.FIFTEEN_MINUTES]: { dateFormat: 'dd MMM HH:mm', rotate: false, showYear: true, tickWidth: 120 },
    [GranularityEnum.ONE_HOUR]: { dateFormat: 'dd MMM HH:mm', rotate: false, showYear: true, tickWidth: 120 },
    [GranularityEnum.ONE_DAY]: { dateFormat: 'dd MMM', rotate: false, showYear: true, tickWidth: 60 },
    [GranularityEnum.ONE_WEEK]: { dateFormat: 'w', rotate: false, showYear: true, tickWidth: 80 },
    [GranularityEnum.ONE_MONTH]: { dateFormat: 'MMM', rotate: false, showYear: true, tickWidth: 70 },
    [GranularityEnum.ONE_YEAR]: { dateFormat: 'yyyy', rotate: false, showYear: false, tickWidth: 40 },
  },
}

const getTickFormatting = (granularity: GranularityEnum, timeFrame: GraphTimeFrameEnum): TickFormatting => {
  const formatting = formatMapping[timeFrame]?.[granularity]
  if (!formatting) {
    console.warn(`No formatting found for timeFrame: ${timeFrame} and granularity: ${granularity}. Using default.`)
    return { dateFormat: 'dd MMM yyyy', rotate: false, showYear: true, tickWidth: 90 }
  }
  return formatting
}

const parseDate = (dateString: string): Date => {
  // Check if the string is just a time (HH:mm)
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
  if (timeRegex.test(dateString)) {
    const [hours, minutes] = dateString.split(':').map(Number)
    const today = startOfToday()
    return setMinutes(setHours(today, hours), minutes)
  }

  // Use parseISO for all other date formats
  const parsedDate = parseISO(dateString)

  if (isValid(parsedDate)) {
    return parsedDate
  }

  return new Date()
}

const calculateDataResolution = (dates: Date[]): DataResolutionEnum => {
  if (dates.length < 2) return DataResolutionEnum.FIFTEEN_MINUTES

  const startDate = new Date(dates[0])
  const endDate = new Date(dates[1])

  const minutesDiff = differenceInMinutes(endDate, startDate)
  if (minutesDiff <= 15) return DataResolutionEnum.FIFTEEN_MINUTES

  const hoursDiff = differenceInHours(endDate, startDate)
  if (hoursDiff <= 1) return DataResolutionEnum.ONE_HOUR

  const daysDiff = differenceInDays(endDate, startDate)
  if (daysDiff <= 1) return DataResolutionEnum.ONE_DAY

  const weeksDiff = differenceInWeeks(endDate, startDate)
  if (weeksDiff <= 1) return DataResolutionEnum.ONE_WEEK

  const monthsDiff = differenceInMonths(endDate, startDate)
  if (monthsDiff <= 1) return DataResolutionEnum.ONE_MONTH
  return DataResolutionEnum.ONE_YEAR
}
